<template>
  <div
    :class="bem({updating})"
  >
    <VisionTag
    v-if="price < originalPrice && price"
    :class="bem('discount-tag')"
    theme="gradient"
    size="small">
      Save {{ $tca(originalPrice - price) }}
    </VisionTag>
    <div :class="bem('image-container')">
      <template v-if="staticImg.path">
        <img
          :src="staticImg.path"
          :alt="staticImg.alt"
          :title="staticImg.title"
          :style="
            hasCustomMediaDimensions
              ? {
                width: customMediaDimensions.width,
                height: customMediaDimensions.height,
              }
              : ''
          "
          :width="customMediaDimensions.width"
          :height="customMediaDimensions.height"
          :class="
            bem('image', {
              hasCustomMediaDimensions,
            })
          "
        />
        <div
          v-if="upsell.polarisationUpsell"
          :class="bem('image-swatch')"
        >
          <BaseChoice
            :key="upsell.product.lensChoice.sku"
            :styles="upsell.product.lensChoice.swatch.styles"
            :image="upsell.product.lensChoice.swatch.image"
            :is-active="false"
            :is-limited-edition="upsell.product.lensChoice.isLimitedEdition"
            :disabled="
              !upsell.product.lensChoice.isOnPreorder &&
                !upsell.product.lensChoice.isInStock
            "
            :recycled="upsell.product.lensChoice.isRecycled"
            shape="circle"
            :disable-infinite-swatch-icon="true"
            full-width
          />
        </div>
      </template>
      <template v-else-if="sku">
        <DynamicImg
          :sku="sku"
          :width="160"
          :height="160"
          :class="bem('image')"
          @loaded="handleImageLoad"
        />
        {{ imageLoaded }}
        <div
          v-if="!imageLoaded"
          :class="[bem('image-shimmer'), 'vision-shimmer']"
        />
      </template>
    </div>
    <div :class="bem('text-container')">
      <VisionHeading
        size="h6"
        tag="h4"
        :class="bem('title')"
      >
        <!-- eslint-disable vue/no-v-html -->
        <span v-html="title" />
        <p
          :class="bem('subtitle')"
          v-html="subtitle"
        />
      <!-- eslint-enable vue/no-v-html -->
      </VisionHeading>
      <div
        :class="bem('price-container')"
      >
        <VisionHeading
          v-if="originalPrice === 0 || originalPrice === price"
          size="h6"
          tag="h4"
          colour-style="gradient"
        >
          {{ `${$tca(price)}` }}
        </VisionHeading>
        <template v-else>
          <s :class="bem('strike-through-price')">
            {{ $tca(originalPrice) }}
          </s>
          <VisionHeading
            size="h6"
            tag="h4"
            colour-style="gradient"
            :class="bem('price-free')"
          >
            {{ price ? $tca(price) : 'FREE' }}
          </VisionHeading>
        </template>
      </div>
      <div :class="bem('content-slot')">
        <slot name="content" />
      </div>
    </div>
    <VisionLink
      :is-button="true"
      :class="bem('button-target')"
      @click="handleClick"
    >
      <span :class="bem('button-inner')">
        <VisionIcon
          icon="plus"
          size="stretch"
          :class="bem('button-icon')"
        />
      </span>
    </VisionLink>
  </div>
</template>

<script setup lang="ts">
import { buildCartItems } from '~/assets/js/helpers/shopify'

const { $sentry, $track } = useNuxtApp()
const shopifyStore = useShopifyStore()
const bem = useBEM('UpsellItemBase')

const props = defineProps({
  title: { type: String, required: false, default: '' },
  subtitle: { type: String, required: false, default: '' },
  sku: { type: String, required: false, default: '' },
  cartExternallyUpdating: { type: Boolean, required: false, default: false },
  price: { type: Number, required: false, default: 0 },
  originalPrice: { type: Number, required: false, default: 0 },
  selected: { type: Boolean, required: false, default: false },
  upsell: { type: Object, required: false, default: () => ({}) },
  staticImg: { type: Object, required: false, default: () => ({}) },
  customMediaDimensions: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  showLensSwatch: { type: Boolean, default: false, required: false },
  index: { type: Number, required: false, default: 0 },
  discount: { type: Object, required: false, default: () => {} }
})

const emit = defineEmits(['removeFromCart', 'addToCart'])

const updating = ref<boolean>(false)
const imageLoaded = ref<boolean>(false)

const hasCustomMediaDimensions = computed(() => {
  return Object.keys(props.customMediaDimensions).length !== 0
})

function handleClick() {
  if (props.upsell.mode === 'lens_type') {
    emit('addToCart')
    return
  }

  addToCart()
}

async function addToCart() {
  updating.value = true

  const attributes = [
    {
      key: '_isUpsellItem',
      value: 'true',
    },
  ]

  try {
    if(props.discount?.codeDetails?.code) {
      await shopifyStore.applyCouponCode({
          code: props.discount?.codeDetails?.code,
          silent: true
      })
    }

    const builtCartItems: any = buildCartItems(props.upsell.product, attributes)
    await shopifyStore.addCartItems(builtCartItems)
    $track.product.add(props.upsell.product, {
      location: 'Cart Generic Upsell',
      quantity: 1,
      order: props.index,
    })
  } catch (error) {
    $sentry.captureException(error)
    console.log(error)
  }

  updating.value = false
}

function handleImageLoad() {
  imageLoaded.value = true
}
</script>

<style lang="postcss">
.UpsellItemBase {
  @apply grid items-center gap-2 bg-grey-300 rounded-lg
  min-h-[120px];
  grid-template-columns: auto 1fr;

  &--updating {
    @apply opacity-40;
  }

  &:hover {
    .UpsellItemBase__button-inner {
      @apply border-grey
    }
  }
}

.UpsellItemBase__text-container {
  @apply flex flex-col pr-10 py-4;
}

.UpsellItemBase__subtitle {
  @apply text-grey-600 font-thin mt-1;
}

.UpsellItemBase__price-container {
  @apply flex items-center gap-2 mt-2;
}

.UpsellItemBase__strike-through-price {
  @apply text-grey-500 font-thin text-[12px];
}

.UpsellItemBase__image-container {
  @apply w-24 h-24 relative rounded-md overflow-hidden;
  
  .UpsellItemBase__image--has-custom-media-dimensions {
    @apply absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2
      object-cover;
  }
}

.UpsellItemBase__image-shimmer {
  @apply absolute top-0 left-0 rounded-lg w-full h-full;
}

.UpsellItemBase__image-swatch {
  @apply w-5 h-5
    absolute bottom-1.5 right-1.5 top-auto left-auto
    rounded-full border border-white border-solid pointer-events-none;
}

.UpsellItemBase__button-target {
  @apply absolute right-0 bottom-0 w-12 h-12 p-1;
}

.UpsellItemBase__button-inner {
  --vision-icon-fill: theme(colors.black);
  @apply relative w-full h-full p-3
  bg-white border border-grey-400 rounded-full overflow-hidden
  transition-colors;

  &::after {
    @apply content-[''] absolute top-0 left-0 h-full w-full rounded-full
    bg-gradient scale-x-0 origin-right transition-transform duration-300;
  }

  @screen md {
    &:hover {
      --vision-icon-fill: theme(colors.white);
      &::after {
        @apply scale-x-100 origin-left;
      }
    }
  }
}

.UpsellItemBase__button-icon {
  @apply relative z-[1]
}

.UpsellItemBase__discount-tag {
  @apply absolute left-1 top-0 -translate-y-1/2 z-[2];
}
</style>
